import { ScreenType } from '@accedo/one-plugin-sdk';
import { remify } from '@dynamic/ui';
import { FC, useEffect } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { useNavigation } from '#/hooks/useNavigation';
import { useOnNavigateAway } from '#/hooks/useOnNavigateAway';
import { handleScreenView } from '#/plugins/analytics/analyticsUtils';
import { Path } from '#/service/routes';
import { useAuthStore } from '#/store/auth/auth.store';
import { tb } from '#/store/translation/translation.store';

import { LoginStandard } from './LoginStandard';
import { LoginModal } from './LoginStandard.styled';

const PageHeading = styled.h2`
  font: var(--fontStyleHeadline);
  color: var(--pageHeadingForeground);
  margin: ${remify(15)} 0 ${remify(24)};
  text-align: center;
`;

interface Props {
  closeModal: () => void;
}

/**
 * Login component responsible for rendering a standard (email + password) login form in the modal to be used within the app
 */
export const LoginStandardPopup: FC<Props> = ({ closeModal }) => {
  const { goBack } = useNavigation('LoginStandard');
  const location = useLocation();

  const { isRegistrationEnabled, isForgotPasswordEnabled } = useAuthStore();

  useOnNavigateAway(closeModal);

  const pageHeading = tb('login.pageTitle');

  useEffect(() => {
    // Explicitly added here to keep deps clean and making sure screen view is fired only once
    const screenTitle = tb('login.pageTitle');

    handleScreenView({
      screenType: ScreenType.Login,
      screenHeading: screenTitle,
      screenTitle,
    });
  }, []);

  const handleNavigation = () => {
    // use case: search page -> login (just a popup) -> register (navigates to page) -> login (popup is over the registration page now) -> close with 'X' button -> navigate back to the starting page (not necessarily home).
    if (location.pathname === Path.Registration) {
      goBack();
    }
  };

  const handleCloseAndNavigate = () => {
    handleNavigation();
    closeModal();
  };

  return (
    <LoginModal
      closeOnEsc
      closeOnOverlayClick
      isOpen
      onClose={handleCloseAndNavigate}
    >
      <PageHeading>{pageHeading}</PageHeading>
      <LoginStandard
        hideForgotPassword={!isForgotPasswordEnabled}
        hideRegister={!isRegistrationEnabled}
        onCancel={closeModal}
        onSuccess={handleCloseAndNavigate}
        pageHeading={pageHeading}
      />
    </LoginModal>
  );
};

LoginStandardPopup.displayName = 'LoginStandardPopup';
